import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Save } from "lucide-react";
import { exportBuffer } from "@/lib/audioExport";
import { INSTRUMENTAL, channelOf, sessionChannels } from "@/components/recstudio/sessionEngine";

const btn = "inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-[12px] transition-colors duration-150 hover:bg-white/[0.1] disabled:opacity-40";

/** Save the session · download the whole mixed song or any single track (with its effects) as WAV or MP3. */
export default function ExportPanel({ session }) {
  const { doc, engine, save, saveState } = session;
  const [busy, setBusy] = useState(null);
  const [part, setPart] = useState(doc.tracks[0]?.id || INSTRUMENTAL);
  const parts = [...(doc.instrumental ? [{ id: INSTRUMENTAL, name: `Instrumental · ${doc.instrumental.name}` }] : []), ...doc.tracks];
  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error("Export failed", { description: e.message }); } finally { setBusy(null); }
  };
  const mix = (fmt) => run(`mix-${fmt}`, async () => {
    const only = sessionChannels(engine, doc);
    if (!only.length) throw new Error("Record a take or add an instrumental first.");
    await exportBuffer(await engine.renderMix({ only }), `${doc.title} - full mix`, fmt);
  });
  const one = (fmt) => run(`track-${fmt}`, async () => {
    const idx = channelOf(engine, doc.id, part);
    if (idx == null || !engine.channels[idx].buffer) throw new Error("That track has no audio yet.");
    const name = parts.find((p) => p.id === part)?.name || "track";
    await exportBuffer(await engine.renderMix({ only: [idx], ignoreMute: true, mastered: false }), `${doc.title} - ${name}`, fmt);
  });
  const spin = (k) => (busy === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />);
  return (
    <section data-testid="rec-export-panel" className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-4">
      <p className="font-display text-[15px] font-semibold">Save &amp; export</p>
      <button type="button" data-testid="rec-save-session" onClick={async () => { await save(); toast.success("Session saved"); }} disabled={saveState === "saving"} className={`mt-3 w-full ${btn} border-sky-300/40 bg-sky-500/10`}>
        {saveState === "saving" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save session
      </button>
      <p className="mb-1.5 mt-4 font-mono text-[10px] uppercase tracking-[0.16em] text-white/45">Complete mixed song</p>
      <div className="grid grid-cols-2 gap-1.5">
        <button type="button" data-testid="rec-export-mix-wav" disabled={Boolean(busy)} onClick={() => mix("wav")} className={btn}>{spin("mix-wav")} WAV</button>
        <button type="button" data-testid="rec-export-mix-mp3" disabled={Boolean(busy)} onClick={() => mix("mp3")} className={btn}>{spin("mix-mp3")} MP3</button>
      </div>
      <p className="mb-1.5 mt-4 font-mono text-[10px] uppercase tracking-[0.16em] text-white/45">One track (with its effects)</p>
      <select data-testid="rec-export-part" value={part} onChange={(e) => setPart(e.target.value)} className="mb-1.5 w-full rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-[12px] focus:outline-none">
        {parts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-1.5">
        <button type="button" data-testid="rec-export-track-wav" disabled={Boolean(busy)} onClick={() => one("wav")} className={btn}>{spin("track-wav")} WAV</button>
        <button type="button" data-testid="rec-export-track-mp3" disabled={Boolean(busy)} onClick={() => one("mp3")} className={btn}>{spin("track-mp3")} MP3</button>
      </div>
      <p className="mt-3 text-[10px] text-white/40">WAV is 24-bit studio quality; MP3 is 192 kbps for sharing.</p>
    </section>
  );
}
